import { type Location } from 'history';
import includes from 'lodash/includes';
import isNil from 'lodash/isNil';
import values from 'lodash/values';

import appConfig from 'src/core/config';
import { type Route, routes as appRoutes } from 'src/core/constants/routes';
import { logger } from 'src/utils/datadog-log-wrapper';

import {
  AnalyticEventName,
  type AnalyticEventPayloadDefinition,
} from './eventRegistry';
import { getUrlInfos } from '../urlParser';
import { initAmplitudeEngagement } from './amplitudeEngagement';
import { initAmplitudeSessionReplay } from './amplitudeSessionReplay';

type EventContext = {
  company_id?: string;
  user_id?: string;
  group_id?: string;
  group_wallet_id?: string;
  organisation_id?: string;
};

export type User = {
  id: string;
  intercom_hash: string;
  lang: string;
  fullname: string;
  email: string;
  is_account_owner: boolean;
  is_admin: boolean;
  is_controller: boolean;
  is_requester: boolean;
};

type SignUpUser = {
  id: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  phone: string;
  country: string;
};

type Company = {
  id: string;
  type: string;
  created_from: string;
  organisation: {
    id: string;
  };
};

// Note: user, company and supervisor are optional because we also load
// analytics on our auth pages, where the user isn't necessarily logged in.
type AnalyticsLoadSettings = {
  user?: User;
  company?: Company;
  supervisor?: {
    name: string;
    email: string;
  };
  integrationsSettings?: IntegrationsSettings;
};

export type IntegrationsSettings = {
  All?: boolean;
  'Segment.io'?: boolean;
  'Amplitude (Actions)'?: boolean | Record<string, unknown>;
  'Google Analytics'?: boolean;
  SatisMeter?: boolean;
  'Actions Slack'?: boolean;
  'Webhooks (Actions)'?: boolean;
};

const eventContext: EventContext = {};

const areAnalyticsEnabled = (): boolean => {
  if (!appConfig.segment.key) {
    return false;
  }

  return appConfig.analyticsEnabled;
};

/* No integrationsSettings means all integrations are enabled (ie {All:true})*/
export const loadAnalytics = async ({
  user,
  company,
  supervisor,
  integrationsSettings,
}: AnalyticsLoadSettings) => {
  if (!window.analytics || !window.analytics.load) {
    return;
  }
  await window.analytics.load(appConfig.segment.key, {
    integrations: areAnalyticsEnabled() ? integrationsSettings : { All: false },
  });

  await initAnalyticsContext({ user, company, supervisor });

  const isAmplitudeEnabled =
    areAnalyticsEnabled() &&
    (!window.analytics.integrations ||
      window.analytics.integrations.All ||
      window.analytics.integrations['Amplitude (Actions)']);
  if (isAmplitudeEnabled && isNil(supervisor) && user && company) {
    try {
      window.analytics.ready(() => {
        initAmplitudeSessionReplay({
          segmentInstance: window.analytics,
          amplitudeApiKey: appConfig.amplitudeApiKey,
          sessionReplayOptions: {
            sampleRate: 0,
            serverZone: 'EU',
          },
        });
      });
    } catch (error) {
      logger.error('Failed to setup Amplitude Session Replay', {
        scope: 'app',
        team: 'none',
        error,
      });
    }

    try {
      window.analytics.ready(async () => {
        await initAmplitudeEngagement({
          segmentInstance: window.analytics,
          amplitudeApiKey: appConfig.amplitudeApiKey,
          engagementOptions: {
            serverZone: 'EU',
            locale: (user.lang || 'en') as 'fr' | 'en' | 'de' | 'es',
          },
        });
      });
    } catch (error) {
      logger.error('Failed to setup Engagement', {
        scope: 'app',
        team: 'none',
        error,
      });
    }
  }
};

export const signupUserIdentify = (user: SignUpUser): void => {
  if (!areAnalyticsEnabled()) {
    return;
  }

  if (window.analytics) {
    window.analytics.identify(
      user.id,
      {
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
        first_name: user.firstName,
        last_name: user.lastName,
        phone: user.phone,
        country: user.country,
      },
      {
        context: {
          active: true,
        },
      },
    );
  } else {
    // Sending warning to Datadog (error does not work as it's triggered a lot, likely due to ad blockers or exotic browser)
    logger.warn('Failed to identify user. window.analytics was not found', {
      scope: 'app',
      team: 'none',
      userId: user.id,
    });
  }
};

const userIdentify = (user: User): void => {
  if (window.analytics) {
    window.analytics.identify(user.id, {
      lang: user.lang,
      language: user.lang,
      name: user.fullname,
      email: user.email,
      account_owner: user.is_account_owner,
      administrator: user.is_admin,
      controller: user.is_controller,
      requester: user.is_requester,
    });
  } else {
    // Sending warning to Datadog (error does not work as it's triggered a lot, likely due to ad blockers or exotic browser)
    logger.warn('Failed to identify user. window.analytics was not found', {
      scope: 'app',
      team: 'none',
      userId: user.id,
    });
  }
};

const initAnalyticsContext = async ({
  user,
  company,
  supervisor,
}: AnalyticsLoadSettings): Promise<void> => {
  if (!areAnalyticsEnabled() || !isNil(supervisor)) {
    return;
  }

  if (user) {
    eventContext.user_id = user.id;
    window._fs_csp = true;
  }

  if (company) {
    const entityId =
      company.type === 'branch_currency' ? company.created_from : company.id;
    const walletId = company.id;
    eventContext.company_id = company.id;
    eventContext.group_id = entityId;
    eventContext.group_wallet_id = walletId;
    eventContext.organisation_id = company.organisation.id;
  }

  if (user && company) {
    userIdentify(user);
  }
};

export const track = <EventName extends AnalyticEventName>(
  name: EventName,
  payload?: AnalyticEventPayloadDefinition[EventName],
): void => {
  if (process.env.REACT_APP_LOG_TRACKING_EVENT) {
    // eslint-disable-next-line no-console
    console.log('tracking event:', name);
    // eslint-disable-next-line no-console
    console.log('payload:', payload);
    // eslint-disable-next-line no-console
    console.log('context', eventContext);
  }
  if (!areAnalyticsEnabled()) {
    return;
  }

  if (!includes(values(AnalyticEventName), name)) {
    console.error(`Unknown tracking event ${name}`); // eslint-disable-line no-console
  }

  window?.analytics?.track(name, {
    ...eventContext,
    ...payload,
  });
};

export const group = <EventName extends AnalyticEventName>(
  name: EventName,
  groupId: string,
  payload?: AnalyticEventPayloadDefinition[EventName],
): void => {
  if (process.env.REACT_APP_LOG_TRACKING_EVENT) {
    // eslint-disable-next-line no-console
    console.log('tracking event:', name);
    // eslint-disable-next-line no-console
    console.log('payload:', payload);
  }
  if (!areAnalyticsEnabled()) {
    return;
  }

  if (!includes(values(AnalyticEventName), name)) {
    console.error(`Unknown tracking event ${name}`); // eslint-disable-line no-console
  }

  window.analytics.group(groupId, {
    eventName: name,
    ...eventContext,
    ...payload,
  });
};

type TrackPageOptions = {
  routes?: Record<string, Route>;
  location: Location;
  prevLocation: Location | undefined;
  company?: {
    id: string;
  } & (
    | {
        type: 'initial';
        created_from: null;
      }
    | {
        type: 'branch_entity' | 'branch_currency' | 'branch_expense_entity';
        created_from: string;
      }
  );
  hash?: string;
  project?: string;
};

export const trackPage = ({
  routes = appRoutes,
  location,
  prevLocation,
  company,
  hash,
  project = 'App',
}: TrackPageOptions): void => {
  if (window.Intercom) {
    window.Intercom('update');
  }

  if (!areAnalyticsEnabled()) {
    return;
  }

  const urlInfos = getUrlInfos({ routes, location, prevLocation, project });
  if (!urlInfos) {
    return;
  }

  const properties: {
    title: string;
    path: string;
    group_id?: string;
    group_wallet_id?: string;
    referrer: string | null;
  } = {
    ...urlInfos.properties,
    group_id:
      company?.type === 'branch_currency' ? company.created_from : company?.id,
    group_wallet_id: company?.id,
  };

  const name = hash ? `${urlInfos.name} - ${hash}` : urlInfos.name;
  const context = { ...eventContext, ...properties };

  if (window.analytics && window.analytics.page) {
    // IMPORTANT: be careful, Segment's `page()` method signature is super weird
    // @see: https://segment.com/docs/sources/website/analytics.js/#page
    window.analytics.page(urlInfos.category, name, context);
  }
};

export {
  AnalyticEventName,
  type AnalyticEventPayloadDefinition,
} from './eventRegistry';
